import { useMobile } from '../hooks/useMobile';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Edit2, UserX, UserCheck } from 'lucide-react';
import Sidebar from './Sidebar';
import TeamInvitePanel from './TeamInvitePanel';

const token = () => localStorage.getItem('token');

const RAG = {
  red:   { bg: '#fee2e2', border: '#fca5a5', dot: '#ef4444', color: '#991b1b' },
  amber: { bg: '#fef3c7', border: '#fcd34d', dot: '#f59e0b', color: '#92400e' },
  green: { bg: '#d1fae5', border: '#6ee7b7', dot: '#10b981', color: '#065f46' },
};

const BrokerTeamManager = () => {
  const mobile = useMobile();
  const navigate = useNavigate();

  // Tab
  const [activeTab, setActiveTab] = useState('team');
  const [showInactive, setShowInactive] = useState(false);

  // Team state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });

  // Clients state
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);

  const [error, setError] = useState('');

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoadingTeam(true);
      const res = await fetch('/api/broker/team', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) { setTeamMembers(await res.json()); setError(''); }
      else setError('Failed to fetch team members');
    } catch (err) { setError(err.message); }
    finally { setLoadingTeam(false); }
  }, []);

  const fetchClients = useCallback(async () => {
    if (clientsLoaded) return;
    setLoadingClients(true);
    try {
      const res = await fetch('/api/broker/team/clients', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) { setClients(await res.json()); setClientsLoaded(true); }
      else setError('Failed to fetch clients');
    } catch (err) { setError(err.message); }
    finally { setLoadingClients(false); }
  }, [clientsLoaded]);

  useEffect(() => { fetchTeamMembers(); }, [fetchTeamMembers]);
  useEffect(() => { if (activeTab === 'clients') fetchClients(); }, [activeTab, fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...formData, role: formType };
      const url  = editingId ? `/api/broker/team/${editingId}` : '/api/broker/team';
      const method = editingId ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(body),
      });
      if (res.ok) { fetchTeamMembers(); resetForm(); }
      else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || data.error || (editingId ? 'Failed to update' : 'Failed to add'));
      }
    } catch (err) { setError(err.message); }
  };

  const handleDeactivate = async (memberId) => {
    if (!window.confirm('Deactivate this team member? They will no longer be able to log in.')) return;
    try {
      const res = await fetch(`/api/broker/team/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) fetchTeamMembers();
      else setError('Failed to deactivate');
    } catch (err) { setError(err.message); }
  };

  const handleReactivate = async (member) => {
    if (!window.confirm(`Reactivate ${member.name}? They will be able to log in again.`)) return;
    try {
      const res = await fetch(`/api/broker/team/${member.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ status: 'active' }),
      });
      if (res.ok) { fetchTeamMembers(); setShowInactive(false); }
      else setError('Failed to reactivate');
    } catch (err) { setError(err.message); }
  };

  const handleSendReset = async (member) => {
    if (!window.confirm(`Send a password reset link to ${member.email}?`)) return;
    try {
      const res = await fetch('/api/auth/send-reset-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ user_id: member.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { window.alert(data.message || 'Reset link sent.'); }
      else setError(data.error || 'Failed to send reset link');
    } catch (err) { setError(err.message); }
  };

  const resetForm = () => {
    setShowForm(false); setFormType(null); setEditingId(null);
    setFormData({ name: '', email: '', password: '' });
  };

  const activeMembers   = teamMembers.filter(m => m.status !== 'inactive');
  const inactiveMembers = teamMembers.filter(m => m.status === 'inactive');
  const managers  = activeMembers.filter(m => m.broker_level === 'manager');
  const advisers  = activeMembers.filter(m => m.broker_level === 'adviser');

  // ── Active member row ───────────────────────────────────────────────────────
  const MemberRow = ({ member }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 16px', borderBottom: '1px solid #F7FAFC' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#0e1b2c',
          color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
          {(member.name || '?')[0].toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 600, color: '#0e1b2c', fontSize: 14 }}>{member.name}</div>
          <div style={{ fontSize: 12, color: '#718096' }}>{member.email}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => { setEditingId(member.id); setFormType(member.broker_level);
          setFormData({ name: member.name, email: member.email, password: '' }); setShowForm(true); }}
          style={{ padding: '5px 10px', background: '#EDF2F7', border: 'none', borderRadius: 6,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <Edit2 size={13} /> Edit
        </button>
        <button onClick={() => handleSendReset(member)}
          style={{ padding: '5px 10px', background: '#e0f2fe', color: '#075985', border: 'none',
            borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          🔑 Reset
        </button>
        <button onClick={() => handleDeactivate(member.id)}
          style={{ padding: '5px 10px', background: '#fee2e2', color: '#991b1b', border: 'none',
            borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <UserX size={13} /> Deactivate
        </button>
      </div>
    </div>
  );

  // ── Inactive member row ─────────────────────────────────────────────────────
  const InactiveMemberRow = ({ member }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 16px', borderBottom: '1px solid #F7FAFC', opacity: 0.75 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e2e8f0',
          color: '#a0aec0', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
          {(member.name || '?')[0].toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 600, color: '#4a5568', fontSize: 14 }}>{member.name}</div>
          <div style={{ fontSize: 12, color: '#a0aec0' }}>{member.email}</div>
          <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 1 }}>
            {member.broker_level === 'manager' ? 'Manager' : 'Adviser'} · Deactivated
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => handleReactivate(member)}
          style={{ padding: '5px 12px', background: '#d1fae5', color: '#065f46', border: 'none',
            borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
          <UserCheck size={13} /> Reactivate
        </button>
      </div>
    </div>
  );

  // ── Client row ──────────────────────────────────────────────────────────────
  const ClientRow = ({ client }) => {
    const rag = client.deal_stage_colour ? RAG[client.deal_stage_colour] : null;
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', borderBottom: '1px solid #F7FAFC' }}
        onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#f0fdf4',
            border: '1px solid #86efac', color: '#16a34a', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
            {(client.client_name || '?')[0].toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#0e1b2c', fontSize: 14 }}>{client.client_name}</div>
            <div style={{ fontSize: 12, color: '#718096' }}>{client.client_email}</div>
            {client.broker_name && (
              <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 1 }}>Broker: {client.broker_name}</div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {client.deal_ref && (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', background: '#d1fae5',
              padding: '2px 7px', borderRadius: 9999 }}>{client.deal_ref}</span>
          )}
          {rag && client.deal_stage_name && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
              fontWeight: 600, padding: '3px 8px', borderRadius: 9999,
              background: rag.bg, border: `1px solid ${rag.border}`, color: rag.color }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: rag.dot }} />
              {client.deal_stage_name}
            </span>
          )}
          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
            background: client.client_status === 'active' ? '#d1fae5' : '#fee2e2',
            color: client.client_status === 'active' ? '#065f46' : '#991b1b' }}>
            {client.client_status || 'active'}
          </span>
          <button onClick={() => navigate(`/deals/${client.deal_id}`)}
            style={{ padding: '5px 10px', background: '#EDF2F7', border: 'none', borderRadius: 6,
              cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#374151' }}>
            View Deal →
          </button>
        </div>
      </div>
    );
  };

  // ── Section ─────────────────────────────────────────────────────────────────
  const Section = ({ title, level, members }) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>
          {title} <span style={{ fontSize: 13, fontWeight: 400, color: '#a0aec0' }}>({members.length})</span>
        </h2>
      </div>
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
        {members.length === 0
          ? <div style={{ padding: '20px 16px', color: '#a0aec0', fontSize: 13 }}>No {title.toLowerCase()} yet</div>
          : members.map(m => <MemberRow key={m.id} member={m} />)
        }
      </div>
    </div>
  );

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: mobile ? 0 : 70, paddingTop: mobile ? 64 : 0,
        padding: mobile ? '64px 16px 24px' : '32px 40px', minHeight: '100vh', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 900 }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 26, color: '#0e1b2c' }}>Team & Clients</h1>
              <p style={{ margin: '4px 0 0', color: '#718096', fontSize: 14 }}>
                {activeMembers.length} active team member{activeMembers.length !== 1 ? 's' : ''} · {clients.length > 0 ? `${clients.length} client${clients.length !== 1 ? 's' : ''}` : 'clients'}
              </p>
            </div>
          </div>

          {error && (
            <div style={{ display: 'flex', gap: 10, padding: '12px 16px', background: '#fee2e2',
              borderRadius: 8, marginBottom: 20, color: '#991b1b', fontSize: 13 }}>
              <AlertCircle size={18} /> {error}
              <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b' }}>✕</button>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '2px solid #EDF2F7', marginBottom: 24 }}>
            {[
              { key: 'team',    label: '👔 My Team',    count: activeMembers.length },
              { key: 'clients', label: '👤 Clients',    count: clientsLoaded ? clients.length : null },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ background: 'none', border: 'none', padding: '10px 20px', fontSize: 14, cursor: 'pointer',
                  fontWeight: activeTab === tab.key ? 700 : 500,
                  color: activeTab === tab.key ? '#0e1b2c' : '#718096',
                  borderBottom: activeTab === tab.key ? '3px solid #10b981' : '3px solid transparent',
                  marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6 }}>
                {tab.label}
                {tab.count !== null && tab.count > 0 && (
                  <span style={{ background: activeTab === tab.key ? '#10b981' : '#a0aec0',
                    color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── TEAM TAB ── */}
          {activeTab === 'team' && (
            <>
              {loadingTeam
                ? <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading team…</div>
                : <>
                    <Section title="Managers" level="manager" members={managers} />
                    <Section title="Advisers" level="adviser" members={advisers} />
                    <TeamInvitePanel managers={managers} />

                    {/* Deactivated toggle */}
                    {inactiveMembers.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          onClick={() => setShowInactive(v => !v)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer',
                            color: '#718096', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                            padding: '6px 0', fontWeight: 500 }}>
                          <span style={{ fontSize: 11, transform: showInactive ? 'rotate(90deg)' : 'none',
                            display: 'inline-block', transition: 'transform 0.15s' }}>▶</span>
                          {showInactive ? 'Hide' : 'Show'} deactivated
                          <span style={{ background: '#e2e8f0', color: '#4a5568', fontSize: 11,
                            fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>
                            {inactiveMembers.length}
                          </span>
                        </button>

                        {showInactive && (
                          <div style={{ marginTop: 10, background: '#fff', border: '1px solid #E2E8F0',
                            borderRadius: 10, overflow: 'hidden' }}>
                            {inactiveMembers.map(m => <InactiveMemberRow key={m.id} member={m} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </>
              }
            </>
          )}

          {/* ── CLIENTS TAB ── */}
          {activeTab === 'clients' && (
            <>
              {loadingClients
                ? <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading clients…</div>
                : clients.length === 0
                  ? <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10,
                      padding: '40px', textAlign: 'center', color: '#718096' }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>👤</div>
                      <p style={{ margin: 0, fontSize: 15 }}>No client portal accounts yet.</p>
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: '#a0aec0' }}>
                        Clients appear here once they've been invited to the client portal via a deal.
                      </p>
                    </div>
                  : <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ padding: '10px 16px', background: '#F7FAFC', borderBottom: '1px solid #EDF2F7',
                        display: 'grid', gridTemplateColumns: '1fr 280px',
                        fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: '#a0aec0' }}>
                        <span>Client</span>
                        <span style={{ textAlign: 'right' }}>Deal / Stage / Status</span>
                      </div>
                      {clients.map(c => <ClientRow key={c.deal_id} client={c} />)}
                    </div>
              }
            </>
          )}

          {/* ── ADD / EDIT FORM MODAL ── */}
          {showForm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={resetForm}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, maxWidth: '90vw' }}
                onClick={e => e.stopPropagation()}>
                <h2 style={{ margin: '0 0 16px', fontSize: 18, color: '#0e1b2c' }}>
                  {editingId ? 'Edit' : 'Add'} {formType === 'manager' ? 'Broker Manager' : 'Broker Adviser'}
                </h2>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { label: 'Name', key: 'name', type: 'text', placeholder: 'Full name' },
                    { label: 'Email', key: 'email', type: 'email', placeholder: 'Email address' },
                    { label: editingId ? 'New password (leave blank to keep)' : 'Password', key: 'password', type: 'password', placeholder: editingId ? 'Leave blank to keep current' : 'Password' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 4 }}>{f.label}</label>
                      <input type={f.type} value={formData[f.key]} placeholder={f.placeholder}
                        onChange={e => setFormData({ ...formData, [f.key]: e.target.value })}
                        required={f.key !== 'password' || !editingId}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px',
                          border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14 }} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button type="button" onClick={resetForm}
                      style={{ flex: 1, padding: '10px', background: '#F7FAFC', border: '1px solid #E2E8F0',
                        borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                    <button type="submit"
                      style={{ flex: 1, padding: '10px', background: '#10b981', color: '#fff',
                        border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                      {editingId ? 'Save Changes' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default BrokerTeamManager;
